import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const LABEL = { new: "New", reviewing: "Reviewing", intro_call: "Intro call", accepted: "Accepted", declined: "Declined" };
const TONE = { new: "bg-[#e0f2f7] text-[#005f7b]", reviewing: "bg-[#fef3c7] text-[#713f12]", intro_call: "bg-[#dcfce7] text-[#14532d]", accepted: "bg-[#dcfce7] text-[#14532d]", declined: "bg-[#f3f4f6] text-[#374151]" };
const STATES = ["CA", "TX", "FL", "AZ", "OH", "OTHER"];
const SEL = "min-h-[40px] rounded-md border border-[#cbd5e1] bg-white px-2 text-[14px]";
const date = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const decode = (s) => { const t = document.createElement("textarea"); t.innerHTML = s || ""; return t.value; };

const download = async (url, name) => {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) { toast.error("Download failed"); return; }
  const b = await r.blob(); const a = document.createElement("a");
  a.href = URL.createObjectURL(b); a.download = name; a.click(); URL.revokeObjectURL(a.href);
};

const SIGN = "\n\nBest regards,\nDirk\n589 Labs | MySchoolRx";
const TEMPLATES = [
  { key: "thanks", label: "Thanks, we got your application", status: "reviewing",
    subject: () => "Thanks for your MySchoolRx Founding State Partner application",
    body: (first, st) => `Hi ${first},\n\nThank you for applying to become a MySchoolRx Founding State Partner${st ? ` for ${st}` : ""}. We've received your application and are reviewing it now.\n\nWe'll be in touch soon about next steps. In the meantime, feel free to reply to this email with any questions.${SIGN}` },
  { key: "invite", label: "Intro call invite", status: "intro_call",
    subject: () => "Let's set up an intro call — MySchoolRx Founding State Partner",
    body: (first, st) => `Hi ${first},\n\nThanks again for your interest in becoming a MySchoolRx Founding State Partner${st ? ` for ${st}` : ""}. We enjoyed reading your application and would love to set up a 30-minute intro call to get to know you and walk through the partnership.\n\nCould you reply with two or three times over the next week that work for you (with your time zone)? We'll send a calendar invite to confirm.${SIGN}` },
];
const ORDER = ["new", "reviewing", "intro_call", "accepted", "declined"];
const draft = (a, t) => {
  const first = decode(a.name).split(" ")[0];
  const st = (a.state_names || []).map(decode).join(", ");
  return `mailto:${a.email}?subject=${encodeURIComponent(t.subject())}&body=${encodeURIComponent(t.body(first, st))}`;
};

const ReplyTemplates = ({ a, patch }) => {
  const [pending, setPending] = useState(null);
  useEffect(() => setPending(null), [a.id]);
  const confirm = async () => {
    if (ORDER.indexOf(pending.status) > ORDER.indexOf(a.status)) await patch({ status: pending.status });
    else toast.success("Noted — status left as it is");
    setPending(null);
  };
  return (
    <div className="mt-4" data-testid="careers-reply-templates">
      <span className="block text-[14px] font-semibold">Reply templates</span>
      <p className="mt-0.5 text-[12px] text-[#4b5f68]">Opens a ready-made draft in your email app. Review it, then press send.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {TEMPLATES.map((t) => (
          <a key={t.key} href={draft(a, t)} onClick={() => setPending(t)} className="inline-flex min-h-[40px] items-center rounded-md border-2 border-[#007a9e] px-4 text-[14px] font-semibold text-[#005f7b] hover:bg-[#eef6f9]" data-testid={`careers-reply-${t.key}`}>{t.label}</a>
        ))}
      </div>
      {pending && (
        <div role="status" className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-[#bfe3ee] bg-[#eef6f9] px-3 py-2 text-[14px]" data-testid="careers-reply-confirm">
          <span>Did you send the “{pending.label}” email? Status will change to <b>{LABEL[pending.status]}</b>.</span>
          <button type="button" onClick={confirm} className="min-h-[36px] rounded-md bg-[#007a9e] px-3 font-semibold text-white" data-testid="careers-reply-confirm-yes">Yes, I sent it</button>
          <button type="button" onClick={() => setPending(null)} className="min-h-[36px] rounded-md px-3 font-semibold text-[#005f7b]" data-testid="careers-reply-confirm-no">Not yet</button>
        </div>
      )}
    </div>
  );
};

const Detail = ({ a, onSaved }) => {
  const [notes, setNotes] = useState(decode(a.admin_notes));
  useEffect(() => setNotes(decode(a.admin_notes)), [a.id, a.admin_notes]);
  const patch = async (body) => {
    const r = await fetch(`${API}/api/admin/careers/applicants/${a.id}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!r.ok) { toast.error("Could not save"); return; }
    onSaved(await r.json()); toast.success("Saved");
  };
  const row = (k, v) => <div className="grid grid-cols-[150px_1fr] gap-2 py-1 text-[14px]"><span className="font-semibold text-[#4b5f68]">{k}</span><span className="break-words text-[#0f2530]">{v || "—"}</span></div>;
  return (
    <div className="mt-4 rounded-xl border border-[#e2e8f0] bg-white p-5" data-testid="careers-applicant-detail">
      <h2 className="text-[18px] font-bold">{decode(a.name)}</h2>
      <div className="mt-2">
        {row("Email", <a className="text-[#005f7b] underline" href={`mailto:${a.email}`}>{a.email}</a>)}
        {row("Phone", decode(a.phone))}
        {row("State(s)", (a.state_names || []).map(decode).join(", "))}
        {row("Current / last role", decode(a.role))}
        {row("LinkedIn", a.linkedin_url ? <a className="text-[#005f7b] underline" href={decode(a.linkedin_url)} target="_blank" rel="noreferrer">{decode(a.linkedin_url)}</a> : null)}
        {row("Network note", <span className="whitespace-pre-line">{decode(a.network_note)}</span>)}
        {row("Received", new Date(a.created_at).toLocaleString())}
        {row("Resume", a.has_resume ? <button type="button" className="font-semibold text-[#005f7b] underline" onClick={() => download(`${API}/api/admin/careers/applicants/${a.id}/resume`, `${decode(a.name)}-resume.${a.resume_ext}`)} data-testid="careers-download-resume">Download resume</button> : null)}
      </div>
      <ReplyTemplates a={a} patch={patch} />
      <label htmlFor="cap-status" className="mt-4 block text-[14px] font-semibold">Status</label>
      <select id="cap-status" className={`${SEL} mt-1 w-full`} value={a.status} onChange={(e) => patch({ status: e.target.value })} data-testid="careers-status-select">
        {Object.entries(LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <label htmlFor="cap-notes" className="mt-4 block text-[14px] font-semibold">Internal notes</label>
      <textarea id="cap-notes" rows={4} className="mt-1 w-full rounded-md border border-[#cbd5e1] p-2 text-[14px]" placeholder="Visible to Super Admins only" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="careers-notes" />
      <button type="button" onClick={() => patch({ admin_notes: notes })} className="mt-2 min-h-[40px] rounded-md bg-[#007a9e] px-4 text-[14px] font-semibold text-white" data-testid="careers-notes-save">Save notes</button>
      <p className="mt-3 text-[12px] text-[#4b5f68]">Every view, status change, note, download and export is audit-logged.</p>
    </div>
  );
};

export default function CareersApplicants() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ q: "", state: "", status: "", sort: "newest" });
  const [sel, setSel] = useState(new URLSearchParams(window.location.search).get("id"));
  const [detail, setDetail] = useState(null);
  const qs = new URLSearchParams(f).toString();
  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/admin/careers/applicants?${qs}`, { credentials: "include" });
    if (!r.ok) { setErr(r.status === 403 ? "Super Admin only." : "Could not load applicants."); setRows([]); return; }
    setRows((await r.json()).applicants);
  }, [qs]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (!sel) { setDetail(null); return; }
    fetch(`${API}/api/admin/careers/applicants/${sel}`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then(setDetail);
  }, [sel]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  if (err === "Super Admin only.") return <div className="p-6" data-testid="careers-applicants-denied"><div role="alert" className="rounded-md bg-[#fef2f2] px-4 py-3 text-[#7f1d1d]">This page is for Super Admins only.</div></div>;
  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" data-testid="careers-applicants-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[26px] font-bold text-[#0f2530]">Applicants</h1>
        <button type="button" onClick={() => download(`${API}/api/admin/careers/applicants/export.csv?${qs}`, "founding-state-partner-applicants.csv")}
          className="min-h-[44px] rounded-md border-2 border-[#007a9e] px-4 font-semibold text-[#005f7b]" data-testid="careers-export-csv">Export CSV</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <input aria-label="Search" placeholder="Search name, email, role…" value={f.q} onChange={set("q")} className="min-h-[40px] flex-1 rounded-md border border-[#cbd5e1] px-3 text-[14px]" data-testid="careers-search" />
        <select aria-label="State" className={SEL} value={f.state} onChange={set("state")} data-testid="careers-filter-state"><option value="">All states</option>{STATES.map((s) => <option key={s} value={s}>{s === "OTHER" ? "Other" : s}</option>)}</select>
        <select aria-label="Status" className={SEL} value={f.status} onChange={set("status")} data-testid="careers-filter-status"><option value="">All statuses</option>{Object.entries(LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select aria-label="Sort" className={SEL} value={f.sort} onChange={set("sort")} data-testid="careers-sort"><option value="newest">Newest first</option><option value="oldest">Oldest first</option></select>
      </div>
      {err && <div role="alert" className="mt-4 rounded-md bg-[#fef2f2] px-3 py-2 text-[#7f1d1d]">{err}</div>}
      <div className="mt-4 overflow-x-auto rounded-xl border border-[#e2e8f0] bg-white">
        <table className="w-full min-w-[720px] text-left text-[14px]" data-testid="careers-applicants-table">
          <thead className="bg-[#f1f5f9]"><tr>{["Received", "Name", "State(s)", "Current / last role", "Resume", "Status"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {(rows || []).map((a) => (
              <tr key={a.id} onClick={() => setSel(a.id)} className={`cursor-pointer border-t border-[#eef2f6] hover:bg-[#f8fafc] ${sel === a.id ? "bg-[#eef6f9]" : ""}`} data-testid={`careers-row-${a.id}`}>
                <td className="px-3 py-2.5">{date(a.created_at)}</td>
                <td className="px-3 py-2.5"><button type="button" className="text-left font-semibold text-[#005f7b] underline" onClick={(e) => { e.stopPropagation(); setSel(a.id); }}>{decode(a.name)}</button></td>
                <td className="px-3 py-2.5">{(a.state_names || []).map(decode).join(", ")}</td>
                <td className="px-3 py-2.5">{decode(a.role)}</td>
                <td className="px-3 py-2.5">{a.has_resume ? (a.resume_ext || "").toUpperCase() : "—"}</td>
                <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[12px] font-bold ${TONE[a.status]}`}>{LABEL[a.status]}</span></td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#4b5f68]">No applicants yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {detail && <Detail a={detail} onSaved={(d) => { setDetail(d); setRows((rs) => rs.map((r) => (r.id === d.id ? { ...r, ...d } : r))); }} />}
    </div>
  );
}
